import { useCallback, useRef, useState } from 'react';
import type { GongFlowStepDetailWire } from '@shared/flow/gong-flow-catalog';
import type { StepDraft } from '@shared/flow/enrollment';
import { useStepOverrideEntry, type StepOverrideSnapshot } from './state/step-override-drafts';
import type { StagedStepCopy } from './state/staged-play-config';
import { isStepOverridden, resolveEffectiveStepValues } from './bulk-step-defaults';
import { EMAIL_STEP_TYPES, GONG_STEP_TYPE_LABELS, stepHasSubjectField } from './gong-step-types';

/**
 * One step-override row's MODEL — the SSE-safe half of
 * `StepOverrideEditor.tsx`'s `StepOverrideRow`, lifted out verbatim so a
 * second row VIEW can consume it (plan 2026-09-30-002 D6: "one step editor at
 * two scopes, never two independent editors"). The V1 row
 * (`StepOverrideRow`, unchanged markup) and the V2 Glaze row
 * (`shell-next/rail/StepRow.tsx`) are two renderers
 * over this one hook.
 *
 * This is a PURE MOVE of `StepOverrideEditor.tsx:292-400` as it stood on
 * `origin/master`. Nothing here is new logic, and three properties of that
 * code are load-bearing — do not "modernize" them:
 *
 *  1. **The derivation ladder is a render-time expression, never an effect.**
 *     `localDraft ?? persistedOverride ?? batchDraft ?? stepDefault` is
 *     recomputed every render (`resolveEffectiveStepValues`). There is no
 *     reseed `useEffect` and there must never be one: the draft store is
 *     never reseeded from server data, so the derived value IS the
 *     re-render-proof pattern for this design (see
 *     `docs/v4-system/draft-state-under-sse.md`, and `StepOverrideEditor`'s
 *     own "Draft State Under SSE compliance note").
 *  2. **`lastKnownBodyRef` is compared and set DURING RENDER**, not in an
 *     effect — React's documented "adjust state during render" pattern. It is
 *     how an EXTERNAL write to the body (another mounted surface, or a save
 *     settling back to a refetched persisted value) is told apart from this
 *     row's own typing: `commit` records the body it is about to store before
 *     the store write lands back as a prop, so a render-time mismatch is, by
 *     elimination, external. Converting it to `useEffect` would reseed the
 *     contentEditable one render late and stomp the caret mid-keystroke.
 *  3. **`bodySeedKey` bumps only on an external change or an explicit reset.**
 *     `RichHtmlField`/`RichTextBodyField` only re-touch the DOM when `seedKey`
 *     changes; own typing must never bump it.
 *
 * Every re-render defence lives here so a consumer cannot forget it: the
 * V2 `StepRow` is expected to contain NO `useEffect` at all.
 *
 * The step-type primitives this hook reads (`EMAIL_STEP_TYPES`,
 * `stepHasSubjectField`, `GONG_STEP_TYPE_LABELS`) moved to the sibling leaf
 * `gong-step-types.ts` in the same change — importing them back out of
 * `StepOverrideEditor.tsx` would put this hook and that component on both
 * ends of an `import/no-cycle` error. That file re-exports all three, so no
 * existing import path changed.
 */

/**
 * The hook's input — the exact value set `StepOverrideRow` already receives
 * as props, minus the pure-view ones (`disabled`, `disabledTitle`,
 * `bodyFormat`, `labels`), which never reach the model.
 *
 * `onChangeOverride`/`onResetOverride` keep the V1 row's prop names rather
 * than the context-level `editStepOverride`/`resetStepOverrideToDefault`
 * names: this is a pure move, and the V1 call site passes these straight
 * through.
 */
export interface StepOverrideRowModelInput {
  /** Plain id, NOT an `EnrollmentItem` — only a `seedKey` ingredient (batch scope passes the sentinel `-1`). */
  itemId: number;
  /** The one live flow step this row renders (`GET /api/flow/gong/flows/:flowId/steps`). */
  step: GongFlowStepDetailWire;
  /** This item's persisted override for THIS step, or `undefined` when it has none. */
  override: StepDraft | undefined;
  /** The caller's unsaved entry for one step (`values: null` = reset intent), or `null`. Must be referentially stable while unchanged. */
  localDraftFor: (stepNumber: number) => { values: StepOverrideSnapshot | null } | null;
  /** Send-time save failure message for one step, or `null`. Same stability requirement. */
  saveErrorFor: (stepNumber: number) => string | null;
  /** The subscribe half of `useStepOverrideEntry`'s pair; one subscription covers both reads above. */
  subscribeStep: (stepNumber: number, onChange: () => void) => () => void;
  /** The batch rung, keyed `String(step_order)` — a plain prop READ, never a second subscription. */
  batchStepCopy?: Readonly<Record<string, StagedStepCopy>>;
  /** Fires on every settled keystroke with the FULL effective snapshot; the caller stores it locally (no network). */
  onChangeOverride: (stepNumber: number, stepType: string, values: StepOverrideSnapshot) => void;
  /** "Use flow default" — local intent until Send. */
  onResetOverride: (stepNumber: number, stepType: string) => void;
}

/** What a row VIEW needs. Nothing in here is view-shaped: no classNames, no labels, no markup decisions. */
export interface StepOverrideRowModel {
  /** `localDraft ?? persistedOverride ?? batchDraft ?? stepDefault`, recomputed every render. */
  effective: StepOverrideSnapshot;
  /** Own-draft EXISTENCE, not a value diff — text identical to the default still counts. */
  isOverridden: boolean;
  /** Whether a rep-editable Subject field exists at all (`EMAIL_STEP_TYPES` member AND not a reply step). */
  hasSubject: boolean;
  /** Whether the BODY is an HTML email body ('Body') rather than a dialer/LinkedIn script ('Notes / script'). Stays true for a reply step. */
  isEmailBody: boolean;
  /** Plain-English step-type copy (`Manual Email`, `Call`, …) — never the raw Gong enum. */
  stepTypeLabel: string;
  /** The collapsed header's ` — {subject}` tail, or `null` when there is no subject to preview. */
  subjectPreview: string | null;
  /** Renders as "Failed to save — retry". */
  saveError: string | null;
  /** `RichHtmlField`/`RichTextBodyField` `seedKey` — element-wise compared, so a new array with equal members does not re-seed. */
  bodySeedKey: readonly [number, number, number, number];
  /** Commit one field; the hook merges it into the full effective snapshot before handing it to `onChangeOverride`. */
  commit: (patch: Partial<StepOverrideSnapshot>) => void;
  /** "Use flow default" — bumps the seed so the contentEditable drops the just-cleared text, then fires `onResetOverride`. */
  reset: () => void;
}

export function useStepOverrideRowModel({
  itemId,
  step,
  override,
  localDraftFor,
  saveErrorFor,
  subscribeStep,
  batchStepCopy,
  onChangeOverride,
  onResetOverride,
}: StepOverrideRowModelInput): StepOverrideRowModel {
  // Typing-lag fix (2026-08-20): subscribe directly to THIS row's
  // `${itemId}:${step.step_order}` store entry instead of receiving an
  // already-resolved value threaded down through
  // EnrollQueueContext→…→StepOverrideEditor's own render. This is what makes
  // an edit re-render exactly this row — see `subscribeStep`'s doc comment
  // on `StepOverrideEditorProps`. `subscribe` covers both reads below (the
  // draft and the error live in the same per-key store entry).
  const subscribe = useCallback(
    (onChange: () => void) => subscribeStep(step.step_order, onChange),
    [subscribeStep, step.step_order],
  );
  const getLocalDraft = useCallback(() => localDraftFor(step.step_order), [localDraftFor, step.step_order]);
  const localDraft = useStepOverrideEntry(subscribe, getLocalDraft);
  const getSaveError = useCallback(() => saveErrorFor(step.step_order), [saveErrorFor, step.step_order]);
  const saveError = useStepOverrideEntry(subscribe, getSaveError);
  // v3 rewrite (plan 2026-09-08-002 Part 2, Key Technical Decision 2) — the
  // batch rung is now a plain prop READ, not a subscription: `PlayQueueCard`
  // already re-renders this row's whole subtree when the staged step copy it
  // subscribes to changes (the same posture `copyPrefill`/base-copy already
  // has), so there is nothing here for a second subscription to add. The
  // staged shape (`{stepType, subject, body}`) is adapted to this component's
  // `{subject_draft, body_draft}` shape at this call site, per Key Technical
  // Decision 3.
  const stagedStep = batchStepCopy?.[String(step.step_order)];
  const batchDraft: StepOverrideSnapshot | null = stagedStep
    ? { subject_draft: stagedStep.subject, body_draft: stagedStep.body }
    : null;
  // Bumped by "Use flow default" — an explicit external reset (RichHtmlField's
  // own doc comment: "AI compose, undo, external reset, or entity/action
  // switch"). The derived `effective` value below updates the `value` PROP,
  // but RichHtmlField only re-touches the DOM when `seedKey` itself changes —
  // a static seedKey would leave the contentEditable showing the just-cleared
  // override text until an unrelated remount happened.
  const [resetVersion, setResetVersion] = useState(0);
  // TWO separate facts, deliberately not one flag. `hasSubject` gates the
  // Subject FIELD and is false for a reply step; `isEmailBody` gates how the
  // BODY is described ('Body' + 'HTML override' vs 'Notes / script') and stays
  // true for a reply step, because a reply's body is still an HTML email body.
  // Collapsing them would relabel a reply email's body as a dialer script.
  const hasSubject = stepHasSubjectField(step);
  const isEmailBody = EMAIL_STEP_TYPES.has(step.step_type);
  const stepTypeLabel = GONG_STEP_TYPE_LABELS[step.step_type];

  // The row's derivation rule (plan 2026-08-19-004, Decision #2, extended by
  // plan 2026-09-08-002 Part 2 U6, Key Technical Decision 2):
  // localDraft ?? persistedOverride ?? batchDraft ?? stepDefault. A local
  // entry with `values: null` is the reset-to-default intent — display the
  // defaults even though a persisted override row still exists server-side
  // (Send will DELETE it), and it resolves straight to the flow default
  // without ever consulting the batch rung (Resolved Decision 2).
  const stepDefault: StepOverrideSnapshot = { subject_draft: step.subject, body_draft: step.body };
  const effective = resolveEffectiveStepValues({ localDraft, persistedOverride: override, batchDraft, stepDefault });

  // Own-draft EXISTENCE, not a value diff (Key Technical Decision 3) — see
  // `isStepOverridden`'s doc comment for why a value diff would misfire once
  // the batch rung exists.
  const isOverridden = isStepOverridden({ localDraft, persistedOverride: override });

  const subjectPreview = hasSubject && effective.subject_draft ? effective.subject_draft : null;

  // Finding #6 (adversarial follow-up): RichHtmlField only re-touches the
  // DOM on a seedKey change, so a change to `effective.body_draft` from any
  // source OTHER than this instance's own typing — another mounted surface
  // writing the same shared store entry, or a save settling and the display
  // falling back to a freshly-refetched persisted value — must bump the
  // seed, or the contentEditable keeps showing stale text. Own typing must
  // NEVER bump it (a reseed mid-keystroke resets the caret), which is why
  // `commit` records the body it is about to store BEFORE the store write
  // lands back as a prop: a render-time mismatch between the ref and the
  // derived value is, by elimination, an external change. Render-phase
  // compare-and-set is the react.dev "adjust state during render" pattern
  // (same as EnrollmentItemEditor's sendError reset on item switch).
  const lastKnownBodyRef = useRef(effective.body_draft);
  const [externalVersion, setExternalVersion] = useState(0);
  if (lastKnownBodyRef.current !== effective.body_draft) {
    lastKnownBodyRef.current = effective.body_draft;
    setExternalVersion(v => v + 1);
  }

  const commit = (patch: Partial<StepOverrideSnapshot>) => {
    const next = { ...effective, ...patch };
    // Own typing — see the finding #6 note above.
    lastKnownBodyRef.current = next.body_draft;
    onChangeOverride(step.step_order, step.step_type, next);
  };

  const reset = () => {
    setResetVersion(v => v + 1);
    onResetOverride(step.step_order, step.step_type);
  };

  return {
    effective,
    isOverridden,
    hasSubject,
    isEmailBody,
    stepTypeLabel,
    subjectPreview,
    saveError,
    bodySeedKey: [itemId, step.step_order, resetVersion, externalVersion] as const,
    commit,
    reset,
  };
}
